import type { FreightClass } from "~/shared/schemas/freight-classes-schema.ts";
import type {
	Class,
	QuoteStateType,
} from "~/shared/types/quote-details-types.ts";
import {
	calculateItemDensity,
	getEstClass,
} from "~/shared/utils/commodity-utils.ts";

/**
 * A freight class is considered blank when no real selection has been made
 * (default placeholder id of `0`/`"0"` or an empty name).
 */
const isClassBlank = (cls: Class | undefined): boolean =>
	!cls || cls.id === 0 || cls.id === "0" || !cls.name;

const findFreightClassByValue = (
	freightClassData: FreightClass[],
	estClassValue: number,
): FreightClass | undefined =>
	freightClassData.find((fc) => Number(fc.code) === estClassValue);

/**
 * Vendors are allowed to leave a handling unit's freight class blank. When they
 * do, the backend-estimated class (derived from the item's density) should be
 * sent on their behalf. This returns a quote state where every blank class is
 * replaced with the matching freight class from `freightClassData`, along with
 * `estimatedClassApplied` indicating whether at least one blank class was filled
 * with an estimate. Items that already have a class, or whose density can't
 * produce an estimate, are left untouched. The original quote state reference is
 * returned when nothing changes so downstream memoization is preserved.
 */
export const applyVendorEstimatedClasses = (
	quoteState: QuoteStateType,
	freightClassData: FreightClass[],
): { quoteState: QuoteStateType; estimatedClassApplied: boolean } => {
	if (freightClassData.length === 0) {
		return { quoteState, estimatedClassApplied: false };
	}

	let changed = false;
	const items = quoteState.items.map((item) => {
		if (!isClassBlank(item.class)) return item;

		const density = calculateItemDensity(item, quoteState.measuringType);
		const estClassValue = getEstClass(density);
		if (estClassValue === 0) return item;

		const match = findFreightClassByValue(freightClassData, estClassValue);
		if (!match) return item;

		changed = true;
		return {
			...item,
			class: { id: match.id, name: match.name, code: match.code },
			estClass: estClassValue,
		};
	});

	return {
		quoteState: changed ? { ...quoteState, items } : quoteState,
		estimatedClassApplied: changed,
	};
};
